import PropTypes from "prop-types";
import dayjs from "@shared/dayjs";
import Avatar from "@shared/v2/Avatar";
import Tooltip from "@shared/v2/Tooltip";
import { calculatePercent, determineCorrectPercentColor, truncateTime } from "./reportHelpers";
import { TD, TR } from "./BusinessDashboard/Table/Table";

const AgentReportRow = ({
  report,
  total_calls: totalCalls,
  total_contacted: totalContacted,
  total_emails: totalEmails,
  total_leads: totalLeads,
}) => (
  <TR>
    <TD>
      <a className="tw-flex tw-items-center tw-gap-[8px]" href={`/people/${report.user_path}`}>
        <Avatar size="medium" src={report.avatar} alt={report.agent} />
        <div className="tw-overflow-hidden tw-text-ellipsis">{report.agent}</div>
      </a>
    </TD>

    <TD>
      <span>{report.lead_count}</span>
    </TD>

    <TD>
      <Tooltip
        id="email_count"
        placement="right"
        trigger={
          <span>
            {report.email_count}
            <span
              style={{
                color: determineCorrectPercentColor(
                  totalLeads,
                  totalEmails,
                  report.lead_count,
                  report.email_count,
                ),
                fontSize: "13px",
              }}
            >
              {" "}
              ({calculatePercent(report.lead_count, report.email_count).toFixed(0)}%)
            </span>
          </span>
        }
        content={
          <>
            <p style={{ paddingTop: "5px" }}>
              % of Assigned Leads:{" "}
              <span
                style={{
                  color: determineCorrectPercentColor(
                    totalLeads,
                    totalEmails,
                    report.lead_count,
                    report.email_count,
                  ),
                  fontSize: "13px",
                }}
              >
                ({calculatePercent(report.lead_count, report.email_count).toFixed(0)}%)
              </span>
            </p>
            <p>
              Team Average:{" "}
              <strong>
                <span style={{ fontSize: "13px" }}>
                  ({calculatePercent(totalLeads, totalEmails).toFixed(0)}%)
                </span>
              </strong>
            </p>
          </>
        }
      />
    </TD>

    <TD>
      <Tooltip
        id="call_count"
        placement="right"
        trigger={
          <span>
            {report.call_count}
            <span
              style={{
                color: determineCorrectPercentColor(
                  totalLeads,
                  totalCalls,
                  report.lead_count,
                  report.call_count,
                ),
                fontSize: "13px",
              }}
            >
              {" "}
              ({calculatePercent(report.lead_count, report.call_count).toFixed(0)}%)
            </span>
          </span>
        }
        content={
          <>
            <p style={{ paddingTop: "5px" }}>
              % of Assigned Leads:{" "}
              <span
                style={{
                  color: determineCorrectPercentColor(
                    totalLeads,
                    totalCalls,
                    report.lead_count,
                    report.call_count,
                  ),
                  fontSize: "13px",
                }}
              >
                {" "}
                ({calculatePercent(report.lead_count, report.call_count).toFixed(0)}%)
              </span>
            </p>
            <p>
              Team Average:{" "}
              <strong>
                <span style={{ fontSize: "13px" }}>
                  {" "}
                  ({calculatePercent(totalLeads, totalCalls).toFixed(0)}%)
                </span>
              </strong>
            </p>
          </>
        }
      />
    </TD>

    <TD>
      <Tooltip
        id="contacted"
        placement="right"
        trigger={
          <span>
            {report.contacted_count}
            <span
              style={{
                color: determineCorrectPercentColor(
                  totalLeads,
                  totalCalls,
                  report.lead_count,
                  report.contacted_count,
                ),
                fontSize: "13px",
              }}
            >
              {" "}
              ({calculatePercent(report.lead_count, report.contacted_count).toFixed(0)}%)
            </span>
          </span>
        }
        content={
          <>
            <p style={{ paddingTop: "5px" }}>
              % Assigned Leads:{" "}
              <span
                style={{
                  color: determineCorrectPercentColor(
                    totalLeads,
                    totalCalls,
                    report.lead_count,
                    report.contacted_count,
                  ),
                  fontSize: "13px",
                }}
              >
                {" "}
                ({calculatePercent(report.lead_count, report.contacted_count).toFixed(0)}%)
              </span>
            </p>
            <p>
              Team Average:{" "}
              <strong>
                <span style={{ fontSize: "13px" }}>
                  {" "}
                  ({calculatePercent(totalLeads, totalContacted).toFixed(0)}%)
                </span>
              </strong>
            </p>
          </>
        }
      />
    </TD>

    {/* No % here: text_count includes owned leads assigned before the range, so it
        isn't a subset of lead_count and a percentage of it could exceed 100%. */}
    <TD>
      <span>{report.text_count}</span>
    </TD>

    <TD>
      {report.email_resp_label !== "-" ? (
        <Tooltip
          id="email_resp_time"
          placement="right"
          trigger={<span>{truncateTime(report.email_resp_label)}</span>}
          content={report.email_resp_label}
        />
      ) : (
        <span>{truncateTime(report.email_resp_label)}</span>
      )}
    </TD>

    <TD>
      {report.call_resp_label !== "-" ? (
        <Tooltip
          id="call_resp_time"
          placement="right"
          trigger={<span>{truncateTime(report.call_resp_label)}</span>}
          content={report.call_resp_label}
        />
      ) : (
        <span>{truncateTime(report.call_resp_label)}</span>
      )}
    </TD>

    <TD>
      {report.text_resp_label !== "-" ? (
        <Tooltip
          id="text_resp_time"
          placement="right"
          trigger={<span>{truncateTime(report.text_resp_label)}</span>}
          content={report.text_resp_label}
        />
      ) : (
        <span>{truncateTime(report.text_resp_label)}</span>
      )}
    </TD>

    <TD>{report.login_count}</TD>

    {report.last_login ? <TD>{dayjs(report.last_login).format("MM/DD/YYYY")}</TD> : <TD />}
  </TR>
);

AgentReportRow.propTypes = {
  report: PropTypes.shape({
    agent: PropTypes.string,
    avatar: PropTypes.string,
    call_count: PropTypes.number,
    call_resp_label: PropTypes.string,
    contacted_count: PropTypes.number,
    email_count: PropTypes.number,
    email_resp_label: PropTypes.string,
    last_login: PropTypes.string,
    lead_count: PropTypes.number,
    login_count: PropTypes.number,
    text_count: PropTypes.number,
    text_resp_label: PropTypes.string,
    user_path: PropTypes.string,
  }).isRequired,
  total_calls: PropTypes.number.isRequired,
  total_contacted: PropTypes.number.isRequired,
  total_leads: PropTypes.number.isRequired,
  total_emails: PropTypes.number.isRequired,
};

export default AgentReportRow;
